import { type MailStatusT } from "./../store/slices/inboxSlice";
import apiRequest from "../utils/ApiRequest/apiRequest";
import type { MailDataI } from "../models/models";
import { type SubUserSentMailsT } from "../store/slices/inboxSlice";
import type { ApiResponseObjectT } from "./models";
import type { RootState } from "../store/store";

const RemoveMailFromTrashApiRequest = async (
  payload: RemoveMailFromTrashApiRequestPayloadT
) => {
  const response = await apiRequest(
    "Mails",
    "RemoveMailFromTrash",
    {
      encryptedMailIds: payload,
    },
    "POST"
  );

  return response;
};

const ReactGetSentMailsApiRequest = async (
  payload: ReactGetSentMailsApiRequestPayloadT,
  abortController?: AbortController
): Promise<ApiResponseObjectT<ReactGetSentMailsApiRequestResponseT>> => {
  const response = await apiRequest({
    controller: "Mails",
    functionName: "ReactGetSentMails",
    parameters: payload,
    method: "POST",
    // passing current controller signal
    // in order to allow to cancel it in the future
    signal: abortController?.signal,
  });

  return response;
};

const ReactGetSentMailsCountApiRequest = async (
  payload: ReactGetSentMailsCountApiRequestPayloadT
): Promise<ApiResponseObjectT<ReactGetSentMailsCountApiRequestResponseT>> => {
  const response = await apiRequest(
    "Mails",
    "ReactGetSentMailsCount",
    payload,
    "POST"
  );

  return response;
};

const ReactUpdateSecuritySettingsApiRequest = async (
  payload: ReactUpdateSecuritySettingsApiRequestPayloadT
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "ReactUpdateSecuritySettings",
    payload,
    "POST"
  );

  return response;
};

const ReactUpdatePaymentSettingsApiRequest = async (
  requestBody: ReactUpdatePaymentSettingsApiRequestPayloadT
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "ReactUpdatePaymentSettings",
    requestBody,
    "POST"
  );

  return response;
};

const ReactRemoveInboxMailsApiRequest = async (
  mailList: string[],
  isUndo: boolean
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "ReactRemoveInboxMails",
    {
      mailList,
      isUndo,
    },
    "POST"
  );

  return response;
};

const ReactGetSideInboxFilesApiRequest = async (
  payload: ReactGetSideInboxFilesApiRequestPayloadT
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "ReactGetSideInboxFiles",
    payload,
    "POST"
  );

  return response;
};

const GetMailLabelsApiRequest = async (): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest("Mails", "Labels");

  return response;
};

const CreateLabelApiRequest = async (
  name: string,
  color: string
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "CreateLabel",
    {
      name,
      color,
    },
    "POST"
  );

  return response;
};

const UpdateLabelApiRequest = async (data: {
  id: string;
  name: string;
  color: string;
}): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "UpdateLabel",
    { ...data },
    "POST"
  );

  return response;
};

const DeleteLabelApiRequest = async (
  id: string
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest("Mails", "DeleteLabel", { id }, "POST");

  return response;
};

const LabelMailsApiRequest = async (
  encryptedMailIds: string[],
  labelIds: number[]
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "LabelMails",
    {
      encryptedMailIds,
      labelIds,
    },
    "POST"
  );

  return response;
};

const UnlabelMailsApiRequest = async (
  encryptedMailIds: string[],
  labelIds: number[]
): Promise<ApiResponseObjectT<any>> => {
  const response = await apiRequest(
    "Mails",
    "UnlabelMails",
    {
      encryptedMailIds,
      labelIds,
    },
    "POST"
  );

  return response;
};

const ReactGetEmbedFormLeadDetailsApiRequest = async (
  encryptedMailId: string
): Promise<
  ApiResponseObjectT<ReactGetEmbedFormLeadDetailsApiRequestResponseT>
> => {
  const response = await apiRequest(
    "Mails",
    "ReactGetEmbedFormLeadDetails",
    {
      mailId: encryptedMailId,
    },
    "GET"
  );

  return response;
};

const GetUsersContributersToMailApiRequest = async (
  encryptedMailId: string
): Promise<
  ApiResponseObjectT<GetUsersContributersToMailApiRequestResponseT>
> => {
  const response = await apiRequest(
    "Mails",
    `GetUsersContributersToMail?mailId=${encryptedMailId}`
  );

  return response;
};

const ReactIsMailSavedByUserApiRequest = async (
  mailId: string
): Promise<ApiResponseObjectT<boolean>> => {
  const response = await apiRequest(
    "Mails",
    "ReactIsMailSavedByUser",
    { mailId },
    "POST"
  );

  return response;
};

export type GetUsersContributersToMailApiRequestResponseT = Record<
  string,
  {
    fullName: string;
    lastDate: string;
    quantityOfFiles: number;
    image: string;
  }
>;

export type ReactGetEmbedFormLeadDetailsApiRequestResponseT = {
  field: string;
  value: string;
}[];

export type ReactGetSideInboxFilesApiRequestPayloadT = {
  encryptedMailId: string;
  numberOfFiles: number;
};

export type ReactUpdatePaymentSettingsApiRequestPayloadT = {
  mailId: string;
  paymentSum: number;
  displayName: string;
  paymentCurrency: string;
  paymentOnes: boolean;
  denyPreview: boolean;
  recaptchaResponse: string;
};

export type ReactUpdateSecuritySettingsApiRequestPayloadT = {
  mailId: string;
  downloadsLimit: number;
  password: string;
  isBlockShare: boolean;
  recipientsOnly: boolean;
  previewFiles: boolean;
  collaboration: boolean;
  recaptchaResponse: string;
};

export type ReactGetSentMailsCountApiRequestPayloadT = {
  filterGlobal: string;
  getSubUserSentMails: SubUserSentMailsT[];
  filterType: string | (string | null)[] | null;
  labelIds: string[];
  subUserEmails: (string | null)[];
};

export type ReactGetSentMailsCountApiRequestResponseT = {
  mailsCount: number;
  inbox: number;
  outbox: number;
};

const GetUploadEndpointApiRequest = async (
  payload?: GetUploadEndpointApiRequestPayloadT
): Promise<ApiResponseObjectT<string>> => {
  const response = await apiRequest(
    "Mails",
    "GetUploadEndpoint",
    // @ts-ignore
    {
      realEndpoint: !!payload?.realEndpoint, // returns bucket name if true
      encryptedMailId: payload?.encryptedMailId || "",
      embedPageEncryptedUserId: payload?.userId || "",
    },
    "GET"
  );

  return response;
};

export type RemoveMailFromTrashApiRequestPayloadT = string[];
export type ReactGetSentMailsApiRequestPayloadT = {
  filterGlobal: Omit<
    keyof RootState["inbox"]["searchParams"],
    "t" | "f" | "pn"
  >;
  filterStatus: string;
  searchQuery: string;
  sorting: {
    by: RootState["inbox"]["searchParams"]["sortBy"];
    asc: boolean;
  };
  pageNumber: number;
  numberOfMails: RootState["inbox"]["searchParams"]["itemsPerPage"];
  numberOfFiles: number;
  includeFilters: MailStatusT[];
  formIds: string[];
  labelIds: string[];
  getSubUserSentMails: SubUserSentMailsT[];
  mailActionStatus: string[];
  subUserEmails: string[];
};
export type ReactGetSentMailsApiRequestResponseT = {
  mailsQuantity: number;
  mailsData: {
    [encryptedMailId: string]: MailDataI;
  };
  /**
   * @deprecated use labels instead
   */
  embedCountStatus: any;
  FTPEnable: boolean;
  APIEnable: boolean;
};

export type GetUploadEndpointApiRequestPayloadT = {
  encryptedMailId?: string;
  userId?: number;
  realEndpoint?: boolean;
};

export {
  RemoveMailFromTrashApiRequest,
  ReactGetSentMailsApiRequest,
  ReactGetSentMailsCountApiRequest,
  ReactUpdateSecuritySettingsApiRequest,
  ReactUpdatePaymentSettingsApiRequest,
  ReactRemoveInboxMailsApiRequest,
  ReactGetSideInboxFilesApiRequest,
  GetMailLabelsApiRequest,
  GetUploadEndpointApiRequest,
  CreateLabelApiRequest,
  UpdateLabelApiRequest,
  DeleteLabelApiRequest,
  LabelMailsApiRequest,
  UnlabelMailsApiRequest,
  ReactGetEmbedFormLeadDetailsApiRequest,
  GetUsersContributersToMailApiRequest,
  ReactIsMailSavedByUserApiRequest,
};
